import { VHVideoConfig } from '../config';
import Plugin from './../core/Plugin';
import log from 'loglevel';
import {
  addClass,
  appendChild,
  createElement,
  removeFromParent,
  getEventPrefix,
  detectCssTransition
} from '../utils/Dom';
import UUID from '../utils/UUID';
import { getTranslateX } from './barrage/utils';
import { isMobile } from '../../common/Utils';

/**
 * @export
 * @class Marquee
 * @extends {Plugin}
 */
export default class Marquee extends Plugin {
  constructor() {
    super();
    this.marqueeConfig = {};
  }

  destroy() {
    super.destroy();
    clearInterval(this.timer);
    this.timer = null;
    const marquee = document.querySelectorAll('.vh-player-marquee');
    for (let i = 0; i < marquee.length; i += 1) {
      removeFromParent(marquee[i]);
    }
  }

  static get type() {
    return 'plugin_marquee';
  }

  init(opts = {}) {
    super.init(opts);
    this._allConfig = opts;
    this.cssTransition = detectCssTransition();
    if (!this.cssTransition) {
      log.warn('不支持 css3 transition');
      return false;
    }
    this.elRoot = document.getElementById(this._allConfig['id']);
    this._defineMethods();
    let style = document.createElement('style');
    style.appendChild(
      document.createTextNode(
        '.vh-player-marquee { z-index: 2; display: inline-block; margin: 0px; padding: 0px; position: absolute; overflow: visible; width: auto; text-overflow: ellipsis; white-space: nowrap; user-select: none; pointer-events: none }'
      )
    );
    // style.innerHTML = '.marquee { z-index: 2; display: inline-block; margin: 0px; padding: 0px; position: absolute; overflow: visible; width: auto; text-overflow: ellipsis; white-space: nowrap; user-select: none; pointer-events: none }';
    document.getElementsByTagName('HEAD').item(0).appendChild(style);
    this.marqueeConfig = JSON.parse(opts[Marquee.type] || VHVideoConfig.plugin_marquee);
    let { enable = false } = this.marqueeConfig;
    if (enable === false) {
      return;
    }
    this.createMarquee();

    this.initEvent();
  }

  initEvent() {
    // 添加事件，处理tab页切换隐藏显示时跑马灯的逻辑
    const prefix = getEventPrefix();
    document.addEventListener(`${prefix}visibilitychange`, this._handleVisibilityChange, false);
  }

  removeEvent() {
    const prefix = getEventPrefix();
    document.removeEventListener(`${prefix}visibilitychange`, this._handleVisibilityChange, false);
  }

  _handleVisibilityChange = () => {
    const prefix = getEventPrefix();
    const visibilityState = prefix ? prefix + 'VisibilityState' : 'visibilityState';
    if (document[visibilityState] === 'visible') {
      if (!this.player || !this.player.player) {
        // 说明实例已经销毁，但是跑马灯事件未销毁
        this.removeEvent();
        this.destroy();
        return;
      }
      // this.elRoot.offsetParent 有值表示video处于可见状态(当元素的 style.display 设置为 "none" 时，offsetParent 返回 null)
      // fixed: safari版本17.1 (19616.2.9.11.7)全屏播放时，offsetParent也是空，增加offsetWidth兼容处理
      if (!this.timer && (this.elRoot.offsetParent || this.elRoot.offsetWidth > 0)) {
        this.createMarquee();
      }
    } else {
      this.destroy();
    }
  };

  _defineMethods() {
    this.player.editMarquee = this.editMarquee.bind(this);
  }

  // 2.3.9 解决 锁屏不触发 visibilitychange 事件，在重复创建dom，但是动画没有执行 （华为-鸿蒙系统）
  checkPosition() {
    const marquee = document.querySelectorAll('.vh-player-marquee');
    for (let i = 0; i < marquee.length; i += 1) {
      const marq = marquee[i];
      let l = getTranslateX(marq);
      if (l === 0) {
        if (marq.offsetWidth > 0 && marq.offsetHeight > 0) {
          // 如果是可见状态，删除该跑马灯, 因为其动画不会执行。
          removeFromParent(marq);
          // 返回true, 创建新的跑马灯
          return true;
        }
        return false;
      }
    }
    return true;
  }

  createMarquee() {
    // displayType 展示方式 0滚动(默认) 1闪动
    // alpha 透明度
    // color 文字颜色
    // interval 时间间隔
    // position 位置，1，2，3，4
    // size 文字大小
    // speed 速度， 后台传过来的是3000，6000，10000， 对应的是快，中，慢
    // text 要显示的文字
    let {
      displayType = 0,
      text = '',
      alpha = 100,
      color = '#ff8d41',
      size,
      unit,
      h5FontSize,
      h5Unit,
      position = 1,
      interval = 20,
      speed = 6000
    } = this.marqueeConfig;

    if (isMobile()) {
      size = h5FontSize;
      unit = h5Unit;
    }

    // 如果跑马灯文本为空，不需要创建
    text = (text || '').trim();
    if (!text) return;
    displayType = parseInt(displayType);
    position = parseInt(position);
    speed = parseInt(speed);
    let labelHeight = size;
    if (parseInt(size) < 12) {
      labelHeight = 12;
    }
    if (displayType !== 0 && displayType !== 1) {
      displayType = 0;
    }
    if (position !== 1 && position !== 2 && position !== 3 && position !== 4) {
      position = 1;
    }
    if (speed !== 3000 && speed !== 6000 && speed !== 10000) {
      speed = 6000;
    }

    const createMarqueeItem = () => {
      // 播放器宽高。 注意：offsetWidth和offsetLeft不能获取隐藏元素的宽高
      // 如果播放器被隐藏了，不继续。 20240722解决某些场景下用户先隐藏播放器，之后显示播放器但是跑马灯不出现的问题。
      const videoWidth = this.elRoot.offsetWidth;
      const videoHeight = this.elRoot.offsetHeight;
      if (videoWidth === 0 || videoHeight === 0) return false;

      if (displayType !== 0) {
        // 闪动
        const marquee = this.elRoot.querySelector('.vh-player-marquee');
        marquee && removeFromParent(marquee);
      } else {
        if (!this.checkPosition()) return false;
      }

      let label = createElement(
        `label_${UUID.create()}`,
        {
          id: `vh-marquee_${UUID.create()}`,
          // innerHTML: text
          textContent: text
        },
        {
          style: `opacity: ${alpha / 100}; font-size: ${size + unit}; color: ${color}; line-height: ${labelHeight}px`
        }
      );
      addClass(label, 'vh-player-marquee');
      appendChild(this._allConfig['id'], label);

      // 跑马灯宽高
      const textWidth = label.offsetWidth;
      const textHeight = label.offsetHeight;
      // 跑马灯宽高文本为0，不继续
      if (textWidth === 0 || textHeight === 0) return false;

      // 跑马灯区域是20%~80%,1随机，2上，3中，4下
      const top2 = videoHeight * 0.2;
      const line = Math.floor((videoHeight * 0.6) / textHeight);
      // 单位：px
      let topArr = [top2];
      for (let i = 1; i <= line; i += 1) {
        topArr.push(topArr[i - 1] + textHeight);
      }

      switch (position) {
        case 1:
          if (displayType === 0) {
            // 随机 滚动
            let index = Math.floor(Math.random() * topArr.length);
            label.style.top = `${topArr[index]}px`;
          } else {
            // 随机 闪动
            if (videoHeight * 0.2 >= textHeight / 2) {
              label.style.top = Math.random() * 60 + 20 + '%';
              label.style.transform = 'translateY(-50%)';
            } else {
              label.style.top = Math.random() * (videoHeight - textHeight) + 'px';
            }
          }
          break;
        // 上
        case 2:
          if (videoHeight * 0.2 >= textHeight / 2) {
            label.style.top = positionMap[position];
            label.style.transform = 'translateY(-50%)';
          } else {
            label.style.top = '0%';
          }
          break;
        // 中
        case 3:
          label.style.top = positionMap[position];
          label.style.transform = 'translateY(-50%)';
          break;
        // 下
        case 4:
          if (videoHeight * 0.2 >= textHeight / 2) {
            label.style.top = positionMap[position];
            label.style.transform = 'translateY(-50%)';
          } else {
            label.style.bottom = '0%';
          }
          break;
        default:
          break;
      }

      if (displayType === 0) {
        label.style.left = '100%';
        // prettier-ignore
        label.style[this.cssTransition.css] = `transform ${ STEP[speed] * ((textWidth + videoWidth) / videoWidth) }s linear`;
        this._stepMoveCss3({ label, textWidth, videoWidth });
        if (position != 1) {
          // prettier-ignore
          const minspeed = STEP[speed] * ((textWidth + videoWidth) / videoWidth) * (textWidth / (textWidth + videoWidth));
          let newInterval = interval > minspeed ? interval : minspeed + 0.5;
          if (this.timer && newInterval != interval) {
            // 清除先前的定时器
            clearInterval(this.timer);
            this.timer = setInterval(createMarqueeItem, newInterval * 1000);
          }
        }
      } else {
        // 闪动的区域
        const moveVal = videoWidth && textWidth && ((videoWidth - textWidth) / videoWidth) * 100;
        const leftValue = moveVal && Math.random() * moveVal;
        label.style.left = `${leftValue}%`;
      }
    };

    if (this.timer) {
      // 清除先前的定时器
      clearInterval(this.timer);
    }
    this.timer = setInterval(createMarqueeItem, interval * 1000);
    createMarqueeItem();
  }

  _stepMoveCss3(mq) {
    mq.label.addEventListener([this.cssTransition.event], () => {
      removeFromParent(mq.label);
    });
    mq.label.style.transform = `translate(-${mq.textWidth + mq.videoWidth}px, 0%)`;
  }

  editMarquee(opt) {
    Object.assign(this.marqueeConfig, opt);
    this.destroy();
    const { enable = false } = this.marqueeConfig;
    if (enable === false || enable === 'false') {
      return;
    }
    this.createMarquee();
  }
}

const positionMap = {
  1: 1,
  2: '20%',
  3: '50%',
  4: '80%'
};

const STEP = {
  3000: 3, // 移动时间 3秒
  6000: 6,
  10000: 12
};

// const STEP = {
//   3000: 32, // 快 每秒移动32%
//   6000: 16, // 中
//   10000: 8 // 慢
// };
